// =============================================================================
// PEDRA — src/core/units.ts
// Conversao entre a unidade interna (Micron = 1/1000 mm) e as unidades do
// usuario, com parsing pt-BR (virgula decimal, ponto de milhar) e imperial
// (fracoes 1/2, 1/4, 1/8, 1/16, 1/32 e pes+polegadas).
// =============================================================================

import type { Micron, Micron2, Unit, DimensionOrder } from './types';

// -----------------------------------------------------------------------------
// CONSTANTES
// -----------------------------------------------------------------------------

/** Quantos micrometros valem 1 unidade de cada escala. Todos exatos. */
export const MICRON_PER_UNIT: Readonly<Record<Unit, number>> = Object.freeze({
  mm: 1_000,
  cm: 10_000,
  m: 1_000_000,
  in: 25_400,
  ft: 304_800,
});

export const MICRON_PER_MM = 1_000;
export const MICRON_PER_INCH = 25_400;
export const MICRON_PER_FOOT = 304_800;

/** Rotulos pt-BR das unidades, para o seletor. */
export const UNIT_LABELS: Readonly<Record<Unit, string>> = Object.freeze({
  mm: 'Milímetros (mm)',
  cm: 'Centímetros (cm)',
  m: 'Metros (m)',
  in: 'Polegadas (")',
  ft: "Pés e polegadas (' \")",
});

/** Sufixo curto exibido depois do numero. */
export const UNIT_SUFFIX: Readonly<Record<Unit, string>> = Object.freeze({
  mm: 'mm',
  cm: 'cm',
  m: 'm',
  in: '"',
  ft: '"',
});

/**
 * Nome por extenso, no plural e em minusculas, para entrar no meio de uma
 * frase ("pequenas demais para milímetros"). `UNIT_LABELS` nao serve: ele e
 * rotulo de seletor, com maiuscula e sigla entre parenteses.
 */
export const UNIT_NOUN: Readonly<Record<Unit, string>> = Object.freeze({
  mm: 'milímetros',
  cm: 'centímetros',
  m: 'metros',
  in: 'polegadas',
  ft: 'pés',
});

/** Casas decimais sugeridas ao trocar de unidade. */
export const DEFAULT_DECIMALS: Readonly<Record<Unit, number>> = Object.freeze({
  mm: 1,
  cm: 2,
  m: 3,
  in: 2,
  ft: 2,
});

/** Casas decimais que representam 1 µm exato em cada unidade metrica. */
const EXACT_DECIMALS: Readonly<Record<Unit, number>> = Object.freeze({
  mm: 3,
  cm: 4,
  m: 6,
  in: 0,
  ft: 0,
});

/** Denominadores de fracao suportados na saida imperial. */
export const FRACTION_DENOMINATORS = [2, 4, 8, 16, 32] as const;

/** true para unidades do sistema imperial (saida em fracao). */
export function isImperial(unit: Unit): boolean {
  return unit === 'in' || unit === 'ft';
}

/**
 * Denominador usado na saida fracionaria, derivado das casas decimais.
 * 0 -> inteiro · 1 -> 1/8 · 2 -> 1/16 · 3 -> 1/32
 */
export function denominatorForDecimals(decimals: number): number {
  const table = [1, 8, 16, 32];
  const i = Math.max(0, Math.min(3, Math.trunc(decimals)));
  return table[i] as number;
}

// -----------------------------------------------------------------------------
// HELPERS INTERNOS
// -----------------------------------------------------------------------------

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x || 1;
}

/** Troca aspas tipograficas, NBSP e sinais unicode por ASCII. */
function normalizeInput(raw: string): string {
  return raw
    .replace(/[″“”«»]/g, '"')
    .replace(/[′‘’]/g, "'")
    .replace(/[      ]/g, ' ')
    .replace(/[−–—]/g, '-')
    .trim();
}

/**
 * Parte inteira -> string de digitos, SEM notacao exponencial.
 * A partir de 1e21 o `String(n)` do JS devolve `"1e+21"`; se isso chegasse ao
 * agrupador de milhares o resultado seria lixo do tipo `"1e.+21"`.
 */
export function intDigits(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const s = String(Math.abs(Math.trunc(n)));
  if (/^\d+$/.test(s)) return s;

  const m = /^(\d)(?:\.(\d+))?e\+(\d+)$/.exec(s);
  if (m) {
    const frac = m[2] ?? '';
    const zeros = Number(m[3]) - frac.length;
    if (zeros >= 0) return (m[1] as string) + frac + '0'.repeat(zeros);
  }
  // Formato inesperado: devolve so os digitos, nunca simbolos soltos.
  const only = s.replace(/\D/g, '');
  return only === '' ? '0' : only;
}

/** Agrupa milhares com ponto (padrao pt-BR). Aceita apenas digitos. */
function groupThousands(digits: string): string {
  const src = /^\d+$/.test(digits) ? digits : intDigits(Number(digits));
  let out = '';
  for (let i = 0; i < src.length; i++) {
    if (i > 0 && (src.length - i) % 3 === 0) out += '.';
    out += src[i];
  }
  return out;
}

/**
 * Interpreta separadores decimais e de milhar de um numero em texto e devolve
 * a string canonica com ponto decimal, ou null se nao for numero.
 *
 * Regras (pt-BR primeiro):
 *  - "." e "," presentes  -> o ULTIMO deles e o decimal, o outro e milhar.
 *  - so ","  -> uma virgula e decimal; varias virgulas sao milhar.
 *  - so "."  -> varios pontos sao milhar; UM ponto e decimal, exceto no padrao
 *               classico de milhar "d.ddd" (1 a 3 digitos, ponto, exatos 3),
 *               que e lido como milhar ("2.400" = 2400).
 *  - espacos internos sao removidos ("2 400,5" = 2400,5).
 *
 * Entrada AMBIGUA e recusada (devolve null) em vez de virar um numero
 * inventado: "1.2.3", "1,,5", "5," e qualquer mistura de separadores fora do
 * padrao de milhar. Isso evita que uma celula quebrada da planilha entre no
 * projeto como uma medida silenciosamente errada.
 */
export function canonicalizeNumber(raw: string): string | null {
  let s = raw.replace(/\s+/g, '');
  if (!s) return null;

  let sign = '';
  if (s.startsWith('-')) {
    sign = '-';
    s = s.slice(1);
  } else if (s.startsWith('+')) {
    s = s.slice(1);
  }
  if (!s) return null;
  if (!/^[\d.,]+$/.test(s)) return null;

  const dots = (s.match(/\./g) ?? []).length;
  const commas = (s.match(/,/g) ?? []).length;

  let decSep: '.' | ',' | null = null;
  if (dots > 0 && commas > 0) {
    decSep = s.lastIndexOf('.') > s.lastIndexOf(',') ? '.' : ',';
  } else if (commas === 1) {
    decSep = ',';
  } else if (dots === 1) {
    decSep = /^\d{1,3}\.\d{3}$/.test(s) ? null : '.';
  }

  let intRaw: string;
  let fracPart = '';
  if (decSep === null) {
    intRaw = s;
  } else {
    const cut = s.lastIndexOf(decSep);
    intRaw = s.slice(0, cut);
    fracPart = s.slice(cut + 1);
  }

  // Depois do separador decimal so pode haver digitos ("1.2.3" cai aqui).
  if (!/^\d*$/.test(fracPart)) return null;
  // "5," / "5." — separador decimal sem casas: numero truncado, nao 5000.
  if (decSep !== null && fracPart === '') return null;

  const intPart = stripThousands(intRaw);
  if (intPart === null) return null;
  if (intPart === '' && fracPart === '') return null;

  return sign + (intPart === '' ? '0' : intPart) + (fracPart ? '.' + fracPart : '');
}

/**
 * Parte inteira -> digitos puros. Aceita digitos corridos ("1234") e o padrao
 * classico de milhar com UM separador consistente ("1.234.567" / "1,234,567").
 * Devolve `null` para agrupamento invalido ("1.2.3", "1,,5", "12.34").
 */
function stripThousands(part: string): string | null {
  if (part === '') return '';
  if (/^\d+$/.test(part)) return part;

  const hasDot = part.includes('.');
  const hasComma = part.includes(',');
  if (hasDot && hasComma) return null; // dois separadores de milhar diferentes

  const re = hasDot ? /^\d{1,3}(?:\.\d{3})+$/ : /^\d{1,3}(?:,\d{3})+$/;
  if (!re.test(part)) return null;
  return part.replace(/[.,]/g, '');
}

/**
 * Converte um numero decimal em texto para Micron SEM passar por float sujo:
 * a parte inteira e multiplicada exatamente e so a fracionaria e arredondada.
 */
function decimalStringToMicron(raw: string, factor: number): Micron | null {
  const canon = canonicalizeNumber(raw);
  if (canon === null) return null;

  const negative = canon.startsWith('-');
  const body = negative ? canon.slice(1) : canon;
  const dot = body.indexOf('.');
  const intPart = dot < 0 ? body : body.slice(0, dot);
  const fracPart = (dot < 0 ? '' : body.slice(dot + 1)).slice(0, 9);

  let value = Number(intPart) * factor;
  if (fracPart) value += Math.round((Number(fracPart) * factor) / 10 ** fracPart.length);
  if (!Number.isFinite(value)) return null;
  return negative ? -value : value;
}

/** "16 1/2" ou "1/2" ou "16" na unidade indicada por `factor`. */
function mixedFractionToMicron(raw: string, factor: number): Micron | null {
  const s = raw.trim();
  const m = /^(?:(\d+)[\s-]+)?(\d+)\s*\/\s*(\d+)$/.exec(s);
  if (!m) return decimalStringToMicron(s, factor);
  const whole = m[1] ? Number(m[1]) : 0;
  const num = Number(m[2]);
  const den = Number(m[3]);
  if (!den || !Number.isFinite(num)) return null;
  return whole * factor + Math.round((num * factor) / den);
}

/** Formato composto imperial: `1' 4 1/2"`, `4'`, `6 1/2"`, `1/2"`. */
function feetInchesToMicron(raw: string): Micron | null {
  let s = raw.trim();
  let sign = 1;
  if (s.startsWith('-')) {
    sign = -1;
    s = s.slice(1).trim();
  }

  let total = 0;
  let matched = false;

  const ft = /^([\d.,]+)\s*'/.exec(s);
  if (ft) {
    const v = decimalStringToMicron(ft[1] as string, MICRON_PER_FOOT);
    if (v === null) return null;
    total += v;
    s = s.slice(ft[0].length).trim();
    matched = true;
  }

  if (s.endsWith('"')) s = s.slice(0, -1).trim();

  if (s.length > 0) {
    const v = mixedFractionToMicron(s, MICRON_PER_INCH);
    if (v === null) return null;
    total += v;
    matched = true;
  }

  if (!matched) return null;
  return sign * total;
}

// -----------------------------------------------------------------------------
// PARSING
// -----------------------------------------------------------------------------

const SUFFIX_PATTERNS: ReadonlyArray<readonly [RegExp, Unit]> = [
  [/\s*(?:mm|milimetros?|milímetros?)$/i, 'mm'],
  [/\s*(?:cm|centimetros?|centímetros?)$/i, 'cm'],
  [/\s*(?:m|metros?)$/i, 'm'],
  [/\s*(?:in|pol|polegadas?)$/i, 'in'],
  [/\s*(?:ft|pes|pés|pe|pé)$/i, 'ft'],
];

/**
 * Converte texto digitado pelo usuario em Micron. Aceita:
 *   "1200" · "16,5" · "16.5" · "2.400,5" · "2 400,5"
 *   "1200mm" · "120 cm" · "1,2 m"   (sufixo explicito sobrepoe `unit`)
 *   `16 1/2"` · `1/2"` · `1' 4 1/2"` · `4'` · "16 1/2" (fracao na unidade atual)
 * Devolve `null` quando o texto nao e uma medida valida.
 */
export function parseDimension(input: string, unit: Unit): Micron | null {
  let s = normalizeInput(input);
  if (!s) return null;

  let effective: Unit = unit;
  for (const [re, u] of SUFFIX_PATTERNS) {
    const stripped = s.replace(re, '');
    if (stripped !== s && stripped.trim() !== '') {
      s = stripped.trim();
      effective = u;
      break;
    }
  }

  // Marcadores imperiais mandam, qualquer que seja a unidade do projeto.
  if (s.includes("'") || s.includes('"')) return feetInchesToMicron(s);

  const factor = MICRON_PER_UNIT[effective];
  if (s.includes('/')) return mixedFractionToMicron(s, factor);
  return decimalStringToMicron(s, factor);
}

/**
 * Converte um valor (texto do usuario ou numero ja na unidade) para Micron.
 * Numeros sao multiplicados direto pelo fator da unidade.
 */
export function toMicron(value: string | number, unit: Unit): Micron | null {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return Math.round(value * MICRON_PER_UNIT[unit]);
  }
  return parseDimension(value, unit);
}

/** Micron -> numero na unidade (float). Use so para calculo/exibicao. */
export function toNumber(v: Micron, unit: Unit): number {
  return v / MICRON_PER_UNIT[unit];
}

// -----------------------------------------------------------------------------
// FORMATACAO
// -----------------------------------------------------------------------------

/** Formata um numero JS no padrao pt-BR (milhar com ".", decimal com ","). */
export function formatNumber(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return '—';
  const negative = value < 0;
  const scale = 10 ** Math.max(0, Math.trunc(decimals));
  const n = Math.round(Math.abs(value) * scale);
  const int = Math.floor(n / scale);
  const frac = n % scale;
  let out = groupThousands(intDigits(int));
  if (decimals > 0) out += ',' + String(frac).padStart(decimals, '0');
  return (negative && n !== 0 ? '-' : '') + out;
}

/** Formata em fracao imperial: `4' 6 1/2"` / `16 1/2"` / `1/2"`. */
export function formatFraction(v: Micron, unit: Unit, denominator: number): string {
  const den0 = Math.max(1, Math.trunc(denominator));
  const negative = v < 0;
  const abs = Math.abs(v);

  const ticks = Math.round((abs * den0) / MICRON_PER_INCH);
  let whole = Math.floor(ticks / den0);
  let num = ticks - whole * den0;
  let den = den0;
  if (num > 0) {
    const g = gcd(num, den);
    num /= g;
    den /= g;
  }

  let feet = 0;
  if (unit === 'ft') {
    feet = Math.floor(whole / 12);
    whole -= feet * 12;
  }

  // ATENCAO: nada de separador de milhar aqui. `1.234 1/2"` seria lido de
  // volta como texto invalido (a fracao gruda no ponto de milhar) e a medida
  // sumiria na reimportacao. Imperial sai sempre com digitos corridos.
  const parts: string[] = [];
  if (feet > 0) parts.push(`${feet}'`);

  let inchText: string;
  if (whole > 0 && num > 0) inchText = `${whole} ${num}/${den}`;
  else if (whole > 0) inchText = String(whole);
  else if (num > 0) inchText = `${num}/${den}`;
  else inchText = feet > 0 ? '' : '0';

  if (inchText) parts.push(inchText + '"');
  return (negative && ticks !== 0 ? '-' : '') + parts.join(' ');
}

/**
 * Micron -> texto na unidade pedida, SEM sufixo em unidades metricas
 * (`"1.200,5"`) e COM os marcadores ' e " nas imperiais (`4' 6 1/2"`),
 * porque neste caso o marcador faz parte da notacao.
 * Em unidades imperiais, `decimals` escolhe o denominador da fracao
 * (ver `denominatorForDecimals`).
 */
export function fromMicron(v: Micron, unit: Unit, decimals: number): string {
  if (!Number.isFinite(v)) return '—';
  if (isImperial(unit)) return formatFraction(v, unit, denominatorForDecimals(decimals));

  const factor = MICRON_PER_UNIT[unit];
  const dp = Math.max(0, Math.min(6, Math.trunc(decimals)));
  const scale = 10 ** dp;
  const negative = v < 0;
  const n = Math.round((Math.abs(v) * scale) / factor);
  const int = Math.floor(n / scale);
  const frac = n % scale;

  let out = groupThousands(intDigits(int));
  if (dp > 0) out += ',' + String(frac).padStart(dp, '0');
  return (negative && n !== 0 ? '-' : '') + out;
}

/**
 * Tira os zeros a direita de um decimal ja formatado em pt-BR:
 * `"3,070"` -> `"3,07"`, `"1,900"` -> `"1,9"`, `"10,00"` -> `"10"`.
 * Nao mexe em texto sem virgula — `"1.200"` e milhar, nao decimal.
 */
export function trimTrailingZeros(text: string): string {
  if (!text.includes(',')) return text;
  const cut = text.replace(/0+$/, '');
  return cut.endsWith(',') ? cut.slice(0, -1) : cut;
}

/**
 * Digitos significativos que uma medida linear nunca perde na exibicao.
 * Com 4, uma chapa de `3,07` sai `"3,07"` mesmo com 1 casa decimal configurada,
 * e `1.234,56` continua saindo `"1.234,6"` — o arredondamento so pode cair
 * onde ele nao muda o numero que o usuario reconhece.
 */
const MIN_SIGNIFICANT_DIGITS = 4;

/**
 * Casas decimais de EXIBICAO de uma medida linear: `decimals` e o piso, e ele
 * sobe (ate o limite da unidade) enquanto estiver escondendo digito
 * significativo. Nunca passa do necessario para o valor ficar exato — poeira
 * de micrometro vinda de conversao (3,070225 m depois de um desvio pela
 * polegada) continua invisivel.
 *
 * Existe por causa de um erro real: com a unidade em mm e 1 casa decimal, uma
 * chapa de 3,07 mm aparecia como `"3,1 mm"` no cabecalho do diagrama enquanto
 * a grade (que formata exato) mostrava `"3,07"`. O mesmo numero, em dois
 * lugares, diferente — e o usuario sem saber qual e o verdadeiro.
 */
function decimalsForDisplay(v: Micron, unit: Unit, decimals: number): number {
  const dp = Math.max(0, Math.min(6, Math.trunc(decimals)));
  const maxDp = EXACT_DECIMALS[unit];
  if (dp >= maxDp || !Number.isFinite(v)) return dp;

  const factor = MICRON_PER_UNIT[unit];
  const abs = Math.abs(Math.round(v));
  const rem = abs % factor;
  if (rem === 0) return dp; // valor redondo: `decimals` ja basta

  // Casas que bastam para o valor ficar exato.
  const fracDigits = String(rem).padStart(maxDp, '0');
  const exactDp = fracDigits.replace(/0+$/, '').length;

  // Casas que bastam para garantir os digitos significativos.
  const int = Math.floor(abs / factor);
  let sigDp: number;
  if (int > 0) {
    sigDp = Math.max(0, MIN_SIGNIFICANT_DIGITS - intDigits(int).length);
  } else {
    const lead = /^0*/.exec(fracDigits)?.[0].length ?? 0;
    sigDp = lead + MIN_SIGNIFICANT_DIGITS;
  }

  return Math.min(maxDp, Math.max(dp, Math.min(sigDp, exactDp)));
}

/**
 * Como `fromMicron`, mas `decimals` vira um PISO ELASTICO em vez de um numero
 * fixo de casas: zeros a direita somem (`3,07 m` sai `"3,07"`, nao `"3,070"`)
 * e digitos significativos nunca somem (`3,07 mm` com 1 casa sai `"3,07"`,
 * nao `"3,1"`).
 *
 * E o formato certo para campo de digitacao, cota e rotulo curto — em metro,
 * casas fixas enchem a tela de zeros que nao dizem nada, e em milimetro elas
 * escondem o que foi digitado. Onde a coluna precisa de numeros alinhados
 * (CSV, relatorio em grade), continue usando `fromMicron`.
 * Em unidades imperiais nao ha o que aparar: a saida ja e fracionaria.
 */
export function fromMicronTrim(v: Micron, unit: Unit, decimals: number): string {
  if (isImperial(unit)) return fromMicron(v, unit, decimals);
  return trimTrailingZeros(fromMicron(v, unit, decimalsForDisplay(v, unit, decimals)));
}

/**
 * `fromMicronTrim` + sufixo da unidade: `"3,07 m"`.
 *
 * Hoje e o MESMO comportamento de `formatLength`, que passou a aparar os zeros
 * a direita. O nome continua existindo porque diz a intencao no ponto de uso
 * ("aqui eu QUERO o numero curto") e porque e o par simetrico de `fromMicronTrim`.
 */
export function formatLengthTrim(v: Micron, unit: Unit, decimals: number): string {
  const text = fromMicronTrim(v, unit, decimals);
  if (isImperial(unit)) return text;
  return `${text} ${UNIT_SUFFIX[unit]}`;
}

// -----------------------------------------------------------------------------
// ESPESSURA DE CORTE (KERF) — SEMPRE EM MILIMETROS
// -----------------------------------------------------------------------------

/**
 * O kerf NAO acompanha a unidade do projeto. Disco de serra, fresa e fio
 * diamantado sao especificados em milimetro no mundo inteiro; num projeto em
 * metro o campo pediria "0,004", que e um convite ao erro de digitacao (um
 * zero a mais vira 0,4 mm; um a menos, 40 mm). Entao o campo le e escreve em
 * mm sempre, qualquer que seja a unidade das medidas.
 */
export const KERF_UNIT: Unit = 'mm';

/** Casas do campo de kerf: 0,05 mm ja e mais fino que qualquer disco real. */
export const KERF_DECIMALS = 2;

/** Micron -> texto do campo de kerf, em mm e sem zeros inuteis: `"4,4"`. */
export function kerfToText(kerf: Micron): string {
  return fromMicronTrim(kerf, KERF_UNIT, KERF_DECIMALS);
}

/**
 * Texto do campo de kerf -> Micron, interpretado em MILIMETROS.
 * Um sufixo explicito ainda manda (`"0,2 cm"` = 2 mm), porque quem digita a
 * unidade sabe o que quer; sem sufixo, e mm.
 */
export function parseKerf(text: string): Micron | null {
  return parseDimension(text, KERF_UNIT);
}

/** Kerf em mm como numero (para exibir em texto corrido). */
export function kerfToMm(kerf: Micron): number {
  return kerf / MICRON_PER_MM;
}

/**
 * Kerf para LEITURA, com a unidade colada: `"4 mm"`, `"4,4 mm"`, `"0 mm"`.
 * Use esta funcao em estatistica, relatorio e PDF — nunca `formatLength` com a
 * unidade do projeto, que num projeto em metro escreveria `"0,004 m"`.
 */
export function formatKerf(kerf: Micron): string {
  return `${kerfToText(kerf)} mm`;
}

// -----------------------------------------------------------------------------
// TEXTO EXATO (IDA E VOLTA SEM PERDA)
// -----------------------------------------------------------------------------

/** Metrico exato: acrescenta casas so quando o valor precisa delas. */
function exactMetric(v: Micron, unit: Unit, minDecimals: number): string {
  const factor = MICRON_PER_UNIT[unit];
  const maxDp = EXACT_DECIMALS[unit];
  const minDp = Math.max(0, Math.min(maxDp, Math.trunc(minDecimals)));

  const negative = v < 0;
  const abs = Math.abs(v);
  const int = Math.floor(abs / factor);
  const rem = abs - int * factor;

  let frac = String(rem).padStart(maxDp, '0').replace(/0+$/, '');
  while (frac.length < minDp) frac += '0';

  const out = groupThousands(intDigits(int)) + (frac ? ',' + frac : '');
  return (negative && abs !== 0 ? '-' : '') + out;
}

/**
 * Imperial exato: a sobra da polegada vira uma fracao de denominador 25400
 * JA SIMPLIFICADA. Medidas nascidas em polegada saem bonitas (`16 1/2"`); as
 * que vieram do metrico saem com denominador incomum, mas voltam exatas.
 */
function exactImperial(v: Micron, unit: Unit): string {
  const negative = v < 0;
  let abs = Math.abs(v);

  let feet = 0;
  if (unit === 'ft') {
    feet = Math.floor(abs / MICRON_PER_FOOT);
    abs -= feet * MICRON_PER_FOOT;
  }
  const whole = Math.floor(abs / MICRON_PER_INCH);
  const rem = abs - whole * MICRON_PER_INCH;

  const parts: string[] = [];
  if (feet > 0) parts.push(`${feet}'`);

  let inchText: string;
  if (rem > 0) {
    const g = gcd(rem, MICRON_PER_INCH);
    const num = rem / g;
    const den = MICRON_PER_INCH / g;
    inchText = whole > 0 ? `${whole} ${num}/${den}` : `${num}/${den}`;
  } else if (whole > 0) {
    inchText = String(whole);
  } else {
    inchText = feet > 0 ? '' : '0';
  }
  if (inchText) parts.push(inchText + '"');

  const zero = feet === 0 && whole === 0 && rem === 0;
  return (negative && !zero ? '-' : '') + parts.join(' ');
}

/**
 * Micron -> texto na unidade pedida SEM PERDA: `parseDimension` do resultado
 * devolve exatamente o mesmo Micron, em qualquer unidade.
 *
 * `fromMicron` arredonda para as casas de exibicao (e proposital na tela);
 * aqui as casas so definem o MINIMO — o que faltar para o valor ser exato e
 * acrescentado. E o formato usado no CSV, que precisa ser reimportavel.
 */
export function toExactText(v: Micron, unit: Unit, minDecimals = 0): string {
  if (!Number.isFinite(v)) return '—';
  const n = Math.round(v);
  return isImperial(unit) ? exactImperial(n, unit) : exactMetric(n, unit, minDecimals);
}

/**
 * Medida linear PARA LEITURA, com o sufixo da unidade: `"1.200,5 mm"`, `"3,07 m"`.
 *
 * `decimals` e TETO, nao piso — e a mesma regra de `formatDimensions`. Zero a
 * direita em medida de peca nao informa nada: num projeto em metro (3 casas por
 * padrao) toda cota viraria `"0,300 m"` no lugar de `"0,3 m"`, e a tela inteira
 * enche de digitos mortos. Onde a coluna precisa de casas FIXAS para alinhar
 * (CSV, relatorio em grade), use `fromMicron` direto.
 */
export function formatLength(v: Micron, unit: Unit, decimals: number): string {
  const text = fromMicronTrim(v, unit, decimals);
  if (isImperial(unit)) return text;
  return `${text} ${UNIT_SUFFIX[unit]}`;
}

// -----------------------------------------------------------------------------
// TOTAIS COM ESCALA ADAPTATIVA (AREA E COMPRIMENTO)
// -----------------------------------------------------------------------------

/**
 * ESCALA ADAPTATIVA — por que existe.
 *
 * Um usuario digitou 3,07 × 1,9 com a unidade em MILIMETRO (uma configuracao
 * salva de outra sessao) achando que estava em metro. O painel respondeu
 * "Área bruta das chapas 0,00 m²", "Área total utilizada 0,00 m²",
 * "Comprimento de corte total 0,00 m". Numeros mudos: nao confirmam, nao
 * negam, nao ajudam a perceber o engano.
 *
 * A regra agora e: m² (e m) continuam sendo a unidade natural do total, mas se
 * o valor for pequeno demais para caber nas casas decimais pedidas — ou seja,
 * se ele fosse sair como "0,00" sem ser zero — a escala desce para uma que
 * mostre o numero de verdade. Zero continua zero em m²/m: nao ha nada a
 * revelar, e trocar a unidade ali so confundiria.
 */
type Scale = readonly [divisor: number, suffix: string];

const AREA_SCALES_METRIC: readonly Scale[] = [
  [MICRON_PER_UNIT.m * MICRON_PER_UNIT.m, 'm²'],
  [MICRON_PER_UNIT.cm * MICRON_PER_UNIT.cm, 'cm²'],
  [MICRON_PER_MM * MICRON_PER_MM, 'mm²'],
];

const AREA_SCALES_IMPERIAL: readonly Scale[] = [
  [MICRON_PER_FOOT * MICRON_PER_FOOT, 'ft²'],
  [MICRON_PER_INCH * MICRON_PER_INCH, 'in²'],
];

const LENGTH_SCALES_METRIC: readonly Scale[] = [
  [MICRON_PER_UNIT.m, 'm'],
  [MICRON_PER_UNIT.cm, 'cm'],
  [MICRON_PER_MM, 'mm'],
];

const LENGTH_SCALES_IMPERIAL: readonly Scale[] = [
  [MICRON_PER_FOOT, 'ft'],
  [MICRON_PER_INCH, 'in'],
];

/**
 * Escolhe a escala e formata. A primeira da lista e a preferida; as outras so
 * entram quando o valor sumiria nela.
 */
function formatScaled(
  value: number,
  scales: readonly Scale[],
  decimals: number,
  minPrimary = 0,
): string {
  if (!Number.isFinite(value)) return '—';
  const dp = Math.max(0, Math.min(6, Math.trunc(decimals)));
  const primary = scales[0] as Scale;

  // Menor valor que ainda vale a pena imprimir na unidade natural.
  const floor = Math.max(0.5 / 10 ** dp, minPrimary);
  const inPrimary = value / primary[0];
  if (value === 0 || Math.abs(inPrimary) >= floor) {
    return `${formatNumber(inPrimary, dp)} ${primary[1]}`;
  }

  // Caiu da unidade natural: prefere a maior escala em que o numero passa de 1
  // (assim as linhas do painel tendem a ficar todas na mesma unidade).
  for (let i = 1; i < scales.length; i++) {
    const [div, suffix] = scales[i] as Scale;
    if (Math.abs(value / div) >= 1) return `${formatNumber(value / div, dp)} ${suffix}`;
  }

  // Ainda minusculo: usa a menor escala e abre casas ate aparecer um digito.
  const [div, suffix] = scales[scales.length - 1] as Scale;
  const v = value / div;
  let extra = dp;
  while (extra < 6 && Math.abs(v) < 0.5 / 10 ** extra) extra += 1;
  return `${formatNumber(v, extra)} ${suffix}`;
}

/**
 * Comprimento "grande" (soma de cortes, metros de fita): metros no sistema
 * metrico, pes no imperial. `"12,35 m"` / `"40,52 ft"`.
 *
 * Escala adaptativa: um total que sairia `"0,00 m"` — ou `"0,04 m"`, que quase
 * nao diz mais — desce para cm ou mm. O piso de 0,05 (5 cm / 0,6") e o ponto
 * em que o metro passa a mostrar um unico digito: dali para baixo o numero
 * informa mais na escala menor.
 */
export function formatBigLength(v: Micron, unit: Unit, decimals = 2): string {
  const scales = isImperial(unit) ? LENGTH_SCALES_IMPERIAL : LENGTH_SCALES_METRIC;
  return formatScaled(v, scales, decimals, 0.05);
}

/**
 * Area em µm² -> m² (metrico) ou ft² (imperial). `"1,44 m²"` / `"12,50 ft²"`.
 *
 * Escala adaptativa: uma area que sairia `"0,00 m²"` desce para cm² ou mm².
 */
export function formatArea(microns2: Micron2, unit: Unit, decimals = 2): string {
  return formatScaled(
    microns2,
    isImperial(unit) ? AREA_SCALES_IMPERIAL : AREA_SCALES_METRIC,
    decimals,
  );
}

/** Percentual pt-BR a partir de uma razao 0..1. `"87,4%"`. */
export function formatPercent(ratio: number, decimals = 1): string {
  if (!Number.isFinite(ratio)) return '—';
  return `${formatNumber(ratio * 100, decimals)}%`;
}

/** Area de um retangulo em µm². */
export function areaOf(width: Micron, length: Micron): Micron2 {
  return width * length;
}

/**
 * Par de dimensoes na ordem configurada: `"2.750 × 1.830 mm"`, `"3,07 × 1,9 m"`.
 * `lengthFirst` (padrao) mostra comprimento antes da largura.
 *
 * Aqui `decimals` e TETO, nao piso: um par de medidas e lido de relance, nao
 * comparado coluna a coluna, e em metro as 3 casas de precisao virariam
 * `"3,070 × 1,900 m"` — tres digitos a mais para dizer a mesma coisa.
 */
export function formatDimensions(
  length: Micron,
  width: Micron,
  unit: Unit,
  decimals: number,
  order: DimensionOrder = 'lengthFirst',
): string {
  const a = fromMicronTrim(order === 'widthFirst' ? width : length, unit, decimals);
  const b = fromMicronTrim(order === 'widthFirst' ? length : width, unit, decimals);
  if (isImperial(unit)) return `${a} × ${b}`;
  return `${a} × ${b} ${UNIT_SUFFIX[unit]}`;
}
